/**
 * Product Features — premium feature card (Savor-native; replaces the GSAP cinematic hero).
 * Native, reliable motions (no GSAP, no scroll-jacking):
 *   - mouse 3D tilt on the image + a light "sheen" that follows the cursor across the card;
 *   - a count-up metric number and a progress ring that fill when the card scrolls into view.
 * The scroll-reveal itself is handled by the fail-safe inline script in the section markup.
 * Reduced motion: tilt/sheen off; the metric shows its final value immediately.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion, clamp } from '@theme/utilities';

class ProductFeaturesComponent extends Component {
  requiredRefs = ['card'];
  #raf = 0;
  #reduced = false;
  #counted = false;

  connectedCallback() {
    super.connectedCallback();
    this.#reduced = prefersReducedMotion();

    // Metric count-up + ring fill when the card enters view.
    if (this.refs.counter || this.refs.ring) this.#observeMetric();

    if (this.#reduced || this.dataset.tilt !== 'true') return;
    window.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    this.refs.card?.addEventListener('pointerleave', this.#reset);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('pointermove', this.#onPointerMove);
    this.refs.card?.removeEventListener('pointerleave', this.#reset);
    cancelAnimationFrame(this.#raf);
  }

  #onPointerMove = (event) => {
    const card = this.refs.card;
    if (!card) return;
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      const rect = card.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) return; // only when on screen
      // Sheen follows the cursor inside the card.
      card.style.setProperty('--mouse-x', `${event.clientX - rect.left}px`);
      card.style.setProperty('--mouse-y', `${event.clientY - rect.top}px`);
      // Tilt the device relative to the cursor's position over the viewport.
      const tilt = this.refs.tilt;
      if (tilt) {
        const x = (event.clientX / window.innerWidth - 0.5) * 2;
        const y = (event.clientY / window.innerHeight - 0.5) * 2;
        tilt.style.transform = `rotateY(${x * 10}deg) rotateX(${-y * 10}deg)`;
      }
    });
  };

  #reset = () => {
    if (this.refs.tilt) this.refs.tilt.style.transform = '';
  };

  #observeMetric() {
    const target = Number(this.dataset.metric) || 0;
    const progress = clamp ? clamp(Number(this.dataset.progress) || 0, 0, 100) : Math.max(0, Math.min(100, Number(this.dataset.progress) || 0));

    const fillRing = () => {
      const ring = this.refs.ring;
      if (!ring) return;
      const circumference = 402; // 2 * pi * r(64)
      ring.style.transition = this.#reduced ? 'none' : 'stroke-dashoffset 1.6s cubic-bezier(.22,.61,.36,1)';
      ring.style.strokeDashoffset = String(circumference * (1 - progress / 100));
    };

    const countUp = () => {
      const el = this.refs.counter;
      if (!el) return;
      if (this.#reduced) { el.textContent = String(target); return; }
      const start = performance.now(), dur = 1600;
      const tick = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        el.textContent = String(Math.round(target * eased));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !this.#counted) {
          this.#counted = true;
          fillRing();
          countUp();
          io.disconnect();
        }
      }
    }, { rootMargin: '0px 0px -15% 0px' });
    io.observe(this.refs.metric ?? this);
  }
}

if (!customElements.get('product-features-component')) {
  customElements.define('product-features-component', ProductFeaturesComponent);
}
